import { createSignal, For, Show, onMount, createMemo } from "solid-js";
import { api, type DebugSessionInfo, type DebugMessageRow } from "../shared";

function copyText(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

export function DebugSessionsList() {
  const [sessions, setSessions] = createSignal<DebugSessionInfo[]>([]);
  const [error, setError] = createSignal("");
  const [selectedSession, setSelectedSession] = createSignal<DebugSessionInfo | null>(null);
  const [messages, setMessages] = createSignal<DebugMessageRow[]>([]);
  const [msgTotal, setMsgTotal] = createSignal(0);
  const [msgLoading, setMsgLoading] = createSignal(false);
  const [page, setPage] = createSignal(0);
  const PAGE_SIZE = 100;

  async function loadSessions() {
    try {
      const list = await api.realtime.debugSessions.list();
      setSessions(list);
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
  }

  onMount(loadSessions);

  async function viewSession(session: DebugSessionInfo) {
    setSelectedSession(session);
    setPage(0);
    setMsgLoading(true);
    try {
      const res = await api.realtime.debugSessions.messages(session.id, { limit: PAGE_SIZE });
      setMessages(res.messages);
      setMsgTotal(res.total);
      setError("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setMsgLoading(false);
    }
  }

  async function loadMore() {
    const next = page() + 1;
    setPage(next);
    try {
      const res = await api.realtime.debugSessions.messages(selectedSession()!.id, { limit: PAGE_SIZE, offset: next * PAGE_SIZE });
      setMessages((prev) => [...prev, ...res.messages]);
    } catch {}
  }

  async function deleteSession(id: string) {
    try {
      await api.realtime.debugSessions.delete(id);
      if (selectedSession()?.id === id) setSelectedSession(null);
      loadSessions();
    } catch {}
  }

  function exportSession() {
    const sess = selectedSession();
    if (!sess) return;
    const data = { session: sess, messages: messages() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `debug-session-${sess.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const hasMore = () => messages().length < msgTotal();

  return (
    <div class="space-y-6">
      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{error()}</span>
          <button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 cursor-pointer">x</button>
        </div>
      </Show>

      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Debug Sessions ({sessions().length})</h3>
        <button onClick={loadSessions} class="px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:text-gray-200 cursor-pointer">Refresh</button>
      </div>

      <div class="border border-gray-800 rounded-lg overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead class="bg-gray-900 text-gray-400">
            <tr>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">ID</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Started</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Messages</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Status</th>
              <th class="text-left px-3 py-2 font-medium border-b border-gray-800">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800/50">
            <For each={sessions()}>
              {(s) => (
                <tr class={`hover:bg-gray-900/30 ${selectedSession()?.id === s.id ? "bg-cyan-900/20" : ""}`}>
                  <td class="px-3 py-2 text-gray-500">{s.id.slice(0, 16)}</td>
                  <td class="px-3 py-2 text-gray-400">{new Date(s.started_at).toLocaleString()}</td>
                  <td class="px-3 py-2 text-gray-300">{s.messageCount ?? "?"}</td>
                  <td class="px-3 py-2">
                    <span class={`${s.ended_at ? "text-gray-500" : "text-emerald-400"}`}>
                      {s.ended_at ? "Ended" : "Active"}
                    </span>
                  </td>
                  <td class="px-3 py-2 flex items-center gap-2">
                    <button onClick={() => viewSession(s)} class="text-cyan-400 hover:text-cyan-300 cursor-pointer">View</button>
                    <button onClick={() => deleteSession(s.id)} class="text-red-400 hover:text-red-300 cursor-pointer">Del</button>
                  </td>
                </tr>
              )}
            </For>
            <Show when={sessions().length === 0}>
              <tr>
                <td colspan={5} class="px-3 py-4 text-center text-gray-600">No debug sessions yet. Connect with "Persist to DB" enabled to create one.</td>
              </tr>
            </Show>
          </tbody>
        </table>
      </div>

      <Show when={selectedSession()}>
        <div class="border border-gray-800 rounded-lg overflow-x-auto">
          <div class="flex items-center justify-between bg-gray-900 px-4 py-2 border-b border-gray-800">
            <div class="flex items-center gap-3 text-xs">
              <span class="font-mono text-cyan-400">{selectedSession()!.id.slice(0, 16)}</span>
              <span class="text-gray-500">{new Date(selectedSession()!.started_at).toLocaleString()}</span>
              <span class="text-gray-500">{msgTotal()} messages</span>
            </div>
            <div class="flex items-center gap-2">
              <button onClick={exportSession} class="px-2 py-1 text-[10px] bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer">Export</button>
              <button onClick={() => setSelectedSession(null)} class="text-gray-500 hover:text-gray-200 cursor-pointer">x</button>
            </div>
          </div>
          <div class="max-h-96 overflow-y-auto">
            <table class="w-full text-[10px] font-mono">
              <thead class="bg-gray-900/50 text-gray-500 sticky top-0">
                <tr>
                  <th class="text-left px-3 py-1.5 font-medium">Time</th>
                  <th class="text-left px-3 py-1.5 font-medium">Type</th>
                  <th class="text-left px-3 py-1.5 font-medium">Sender</th>
                  <th class="text-left px-3 py-1.5 font-medium">Project</th>
                  <th class="text-left px-3 py-1.5 font-medium">Payload</th>
                  <th class="px-1 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800/30">
                <For each={messages()}>
                  {(m) => (
                    <tr class="hover:bg-gray-900/20">
                      <td class="px-3 py-0.5 text-gray-500 whitespace-nowrap">{new Date(m.timestamp).toLocaleTimeString()}</td>
                      <td class="px-3 py-0.5 text-cyan-400">{m.event_type || "?"}</td>
                      <td class="px-3 py-0.5">
                        <span class={`${m.sender_role === "searcher" ? "text-cyan-400" : m.sender_role === "manager" ? "text-purple-400" : "text-gray-400"}`}>
                          {m.sender_role || "?"}
                        </span>
                      </td>
                      <td class="px-3 py-0.5 text-gray-500">{m.project_id?.slice(0, 10) || "—"}</td>
                      <td class="px-3 py-0.5 text-gray-400 max-w-md truncate">{m.payload?.slice(0, 80)}</td>
                      <td class="px-1 py-0.5">
                        <button onClick={() => copyText(m.payload)} class="text-gray-600 hover:text-gray-300 cursor-pointer" title="Copy">📋</button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
          <Show when={hasMore()}>
            <div class="px-4 py-2 border-t border-gray-800 bg-gray-900/30">
              <button
                onClick={loadMore}
                disabled={msgLoading()}
                class="px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs hover:text-gray-200 disabled:opacity-50 cursor-pointer"
              >
                {msgLoading() ? "Loading..." : `Load more (${messages().length} / ${msgTotal()})`}
              </button>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}
